import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useAuth } from "../../contexts/AuthContext";
import { FileSpreadsheet, Download, Trash2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import SharePopover from "../../components/SharePopover";
import DocAttachments from "../../components/DocAttachments";

const today = new Date();
const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
const todayStr = today.toISOString().slice(0, 10);

export default function AccountsSalesRegister() {
    const { user } = useAuth();
    const canDelete = user && (user.role === "admin" || user.role === "editor");
    const [data, setData] = useState(null);
    const [from, setFrom] = useState(firstOfMonth);
    const [to, setTo] = useState(todayStr);
    const [busy, setBusy] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null); // row object
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/accounts/sales-register", { params: { from_date: from, to_date: to } });
            setData(data);
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/invoices/${pendingDelete.ref_id}`);
            toast.success(`Invoice ${pendingDelete.invoice_number} deleted`);
            setPendingDelete(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not delete invoice");
        } finally {
            setDeleting(false);
        }
    };

    const exportXlsx = async () => {
        if (!data) return;
        try {
            const resp = await api.get("/accounts/sales-register.xlsx", {
                params: { from_date: from, to_date: to },
                responseType: "blob",
            });
            const blob = new Blob([resp.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `sales-register-${from}_to_${to}.xlsx`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 1000);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't export Excel");
        }
    };

    return (
        <div data-testid="accounts-sales-register">
            <Toaster position="top-center" />
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700 flex items-center gap-2"><FileSpreadsheet className="w-3.5 h-3.5" />Tally-style</div>
                    <h1 className="font-serif text-4xl text-slate-900 mt-2">Sales Register</h1>
                    <p className="text-sm text-stone-600 mt-2">Period sales with GST split. Use this for monthly reconciliation and CA hand-over.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button onClick={exportXlsx} variant="outline" className="rounded-none border-stone-300" data-testid="sr-export"><Download className="w-4 h-4 mr-2" />Export Excel</Button>
                    <SharePopover
                        text={data
                            ? `Sales Register ${from} → ${to}\n${data.rows.length} invoices · Taxable ${formatINR(data.grand_totals.taxable)} · Total ${formatINR(data.grand_totals.total)}`
                            : `Sales Register ${from} → ${to}`}
                        subject={`Sales Register · ${from} to ${to}`}
                        label="Share"
                        testId="sr-share"
                    />
                </div>
            </div>

            <div className="bg-white border border-sky-100 p-4 mb-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                <div>
                    <Label className="text-[10px] uppercase tracking-widest text-stone-500">From</Label>
                    <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="sr-from" />
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-widest text-stone-500">To</Label>
                    <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="sr-to" />
                </div>
                <div className="sm:col-span-2 text-xs text-stone-500">
                    Quick presets:
                    <button onClick={() => { setFrom(firstOfMonth); setTo(todayStr); }} className="ml-2 underline text-sky-700">This month</button>
                    <button onClick={() => {
                        const q = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
                        setFrom(q.toISOString().slice(0, 10)); setTo(todayStr);
                    }} className="ml-3 underline text-sky-700">This quarter</button>
                    <button onClick={() => {
                        const fy = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
                        setFrom(`${fy}-04-01`); setTo(todayStr);
                    }} className="ml-3 underline text-sky-700">This FY (Apr-Mar)</button>
                </div>
            </div>

            {data && (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                        <Card label="Invoices" value={data.rows.length} />
                        <Card label="Taxable" value={formatINR(data.grand_totals.taxable)} cls="text-stone-900" />
                        <Card label="CGST + SGST" value={formatINR(data.grand_totals.cgst + data.grand_totals.sgst)} cls="text-stone-700" />
                        <Card label="IGST" value={formatINR(data.grand_totals.igst)} cls="text-stone-700" />
                        <Card label="Total" value={formatINR(data.grand_totals.total)} cls="text-sky-700" />
                    </div>

                    {data.summary_by_rate.length > 0 && (
                        <div className="bg-white border border-sky-100 mb-4">
                            <div className="px-3 py-2 bg-sky-50 border-b border-sky-100 text-xs uppercase tracking-widest text-sky-800">Summary by GST rate</div>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-stone-100">
                                        <Th>Rate</Th><Th cls="text-right">Invoices</Th><Th cls="text-right">Taxable</Th>
                                        <Th cls="text-right">CGST</Th><Th cls="text-right">SGST</Th><Th cls="text-right">IGST</Th><Th cls="text-right">Total</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.summary_by_rate.map((s) => (
                                        <tr key={s.gst_rate} className="border-b border-stone-100">
                                            <td className="p-2 font-mono text-xs">{s.gst_rate}</td>
                                            <td className="p-2 text-right">{s.count}</td>
                                            <td className="p-2 text-right font-mono">{formatINR(s.taxable)}</td>
                                            <td className="p-2 text-right font-mono">{formatINR(s.cgst)}</td>
                                            <td className="p-2 text-right font-mono">{formatINR(s.sgst)}</td>
                                            <td className="p-2 text-right font-mono">{formatINR(s.igst)}</td>
                                            <td className="p-2 text-right font-mono text-sky-700">{formatINR(s.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            <div className="bg-white border border-sky-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-sky-50 border-b border-sky-100">
                            <Th>Date</Th><Th>Invoice #</Th><Th>Party</Th><Th>GSTIN</Th>
                            <Th cls="text-right">Taxable</Th><Th cls="text-right">CGST</Th><Th cls="text-right">SGST</Th>
                            <Th cls="text-right">IGST</Th><Th cls="text-right">Total</Th>
                            {canDelete && <Th cls="text-right">Actions</Th>}
                        </tr>
                    </thead>
                    <tbody>
                        {busy ? (
                            <tr><td colSpan={canDelete ? 10 : 9} className="p-8 text-center text-stone-500">Loading…</td></tr>
                        ) : !data?.rows.length ? (
                            <tr><td colSpan={canDelete ? 10 : 9} className="p-8 text-center text-stone-500">No invoices in this period.</td></tr>
                        ) : data.rows.map((r) => (
                            <tr key={r.ref_id} className="border-b border-stone-100 hover:bg-sky-50/40" data-testid={`sr-row-${r.invoice_number}`}>
                                <td className="p-2 text-stone-600 font-mono text-xs whitespace-nowrap">{r.date}</td>
                                <td className="p-2 font-mono text-xs">
                                    <Link to={`/employee/accounts/invoices/${r.ref_id}`} className="text-sky-700 hover:underline">{r.invoice_number}</Link>
                                </td>
                                <td className="p-2 font-medium text-slate-900">{r.party}{r.party_type && <span className="text-[10px] tracking-widest text-stone-500 uppercase ml-2">{r.party_type}</span>}</td>
                                <td className="p-2 font-mono text-xs text-stone-600">{r.gst_number || "—"}</td>
                                <td className="p-2 text-right font-mono">{formatINR(r.taxable)}</td>
                                <td className="p-2 text-right font-mono">{formatINR(r.cgst)}</td>
                                <td className="p-2 text-right font-mono">{formatINR(r.sgst)}</td>
                                <td className="p-2 text-right font-mono">{formatINR(r.igst)}</td>
                                <td className="p-2 text-right font-mono text-sky-700">{formatINR(r.total)}</td>
                                {canDelete && (
                                    <td className="p-2 text-right">
                                        <Button
                                            onClick={() => setPendingDelete(r)}
                                            variant="outline"
                                            size="sm"
                                            className="rounded-none border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 h-8 px-2"
                                            data-testid={`sr-delete-${r.invoice_number}`}
                                            title="Delete invoice"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <section className="mt-6 bg-white border border-stone-200">
                <header className="flex items-center justify-between px-4 py-3 border-b border-stone-100 bg-stone-50">
                    <h2 className="font-serif text-lg text-stone-900">Supporting documents</h2>
                    <span className="text-[11px] text-stone-500">CA exports, GST returns, e-invoice receipts for this period</span>
                </header>
                <div className="p-4">
                    <DocAttachments
                        scope="sales-register"
                        scopeId={`${from}__${to}`}
                        canEdit={user?.role === "admin" || user?.role === "editor" || (user?.capabilities || []).includes("accounts")}
                        testIdPrefix="sr-att"
                    />
                </div>
            </section>

            <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
                <AlertDialogContent className="rounded-none" data-testid="sr-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-serif text-2xl text-red-700">Delete invoice {pendingDelete?.invoice_number}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This permanently removes the invoice <strong>{pendingDelete?.invoice_number}</strong> for <strong>{pendingDelete?.party || "—"}</strong> ({formatINR(pendingDelete?.total || 0)}).
                            Any payments recorded against it will also be removed and the linked order (if any) will be reset to <em>pending</em> so you can re-invoice it.
                            <br /><br />
                            <span className="text-amber-700">
                                ⚠ For production write-offs prefer <em>Cancel</em> from the invoice editor — that keeps the audit trail.
                                Use Delete only for test data, duplicates, or mistakes.
                            </span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none" data-testid="sr-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            disabled={deleting}
                            className="rounded-none bg-red-700 hover:bg-red-800 text-stone-50"
                            data-testid="sr-delete-confirm"
                        >
                            {deleting ? "Deleting…" : "Delete permanently"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

const Th = ({ children, cls = "" }) => <th className={`p-2 text-xs uppercase tracking-widest text-stone-500 ${cls || "text-left"}`}>{children}</th>;
const Card = ({ label, value, cls = "" }) => (
    <div className="bg-white border border-sky-100 p-4">
        <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
        <div className={`font-serif text-xl mt-1 ${cls}`}>{value}</div>
    </div>
);
